// Liste des chantiers des sites autorisés. Un chantier naît d'un devis accepté (Études et devis › devis › « Préparer le
// chantier ») ou, pour une petite pose ou des travaux en régie, directement ici (« sans devis », chargé d'affaires).
import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { FlaskConical, HardHat, Plus } from 'lucide-react';
import { api, messageErreur } from '../../../lib/api.ts';
import { useSession } from '../../../lib/session.tsx';
import { date, depuis, pluriel } from '../../../lib/format.ts';
import { Alerte, Badge, Bouton, Champ, ChampChoix, Chargement, Dialogue, EntetePage, EtatErreur, EtatVide, Panneau } from '../../../components/ui.tsx';
import { BadgeStatutChantier } from '../../../components/statuts.tsx';
import { OngletsInterventions } from './commun.tsx';
import type { ChantierResume } from './types.ts';

const FILTRES = [
  { valeur: 'en_cours', libelle: 'En préparation et en cours' },
  { valeur: 'termines', libelle: 'Terminés et clos' },
  { valeur: 'tous', libelle: 'Tous' },
];

function DialogueSansDevis({ fermer }: { fermer: () => void }) {
  const nav = useNavigate();
  const sites = useQuery({ queryKey: ['sites'], queryFn: () => api.get<{ id: string; name: string }[]>('/api/sites') });
  const [site, setSite] = useState('');
  const [titre, setTitre] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);
  const creer = async () => {
    setErreur(null);
    if (!site) { setErreur('Choisissez le site du chantier.'); return; }
    if (!titre.trim()) { setErreur('Donnez un intitulé au chantier.'); return; }
    setEnvoi(true);
    try {
      const r = await api.post<{ id: string }>('/api/chantiers', { siteId: site, titre: titre.trim() });
      nav(`/interventions/chantiers/${r.id}/preparer`);
    } catch (e) { setErreur(messageErreur(e)); setEnvoi(false); }
  };
  return (
    <Dialogue titre="Nouveau chantier sans devis" ouvert fermer={fermer}
      pied={<><Bouton onClick={fermer}>Annuler</Bouton><Bouton variante="principal" chargement={envoi} onClick={creer}>Créer et préparer</Bouton></>}>
      <div className="pile">
        <p className="petit secondaire" style={{ margin: 0 }}>Pour une petite pose ou des travaux en régie : postes et étapes se saisissent directement. Un chantier chiffré part plutôt de son devis accepté.</p>
        {erreur && <Alerte ton="danger">{erreur}</Alerte>}
        <ChampChoix libelle="Site" value={site} onChange={(e) => setSite(e.target.value)} vide="Choisir un site"
          options={(sites.data ?? []).map((s) => ({ valeur: s.id, libelle: s.name }))} />
        <Champ libelle="Intitulé du chantier" value={titre} maxLength={300} onChange={(e) => setTitre(e.target.value)} placeholder="Ex. : remplacement de deux sondes" />
      </div>
    </Dialogue>
  );
}

export function PageChantiers() {
  const { peut } = useSession();
  const [params, setParams] = useSearchParams();
  const statut = params.get('statut') ?? 'en_cours';
  const [creation, setCreation] = useState(false);
  const liste = useQuery({ queryKey: ['chantiers', statut], queryFn: () => api.get<ChantierResume[]>(`/api/chantiers?statut=${statut}`) });
  return (
    <div className="pile-l">
      <EntetePage titre="Interventions" sousTitre="Chantiers : du devis accepté au travail sur site, poste par poste."
        actions={peut('chantier.preparer') && <Bouton variante="principal" icone={<Plus aria-hidden />} onClick={() => setCreation(true)}>Chantier sans devis</Bouton>} />
      <OngletsInterventions />
      <div className="ch-filtre">
        <ChampChoix libelle="Afficher" value={statut} onChange={(e) => { const p = new URLSearchParams(params); p.set('statut', e.target.value); setParams(p, { replace: true }); }}
          options={FILTRES} />
      </div>
      {liste.isLoading && <Chargement />}
      {liste.isError && <EtatErreur erreur={liste.error} reessayer={() => liste.refetch()} />}
      {liste.data && (
        <Panneau serre titre={pluriel(liste.data.length, 'chantier')}>
          {liste.data.length === 0
            ? <EtatVide icone={<HardHat />} titre="Aucun chantier"
                texte={peut('chantier.preparer')
                  ? 'Un chantier se prépare depuis un devis émis et accepté par le client (Études et devis › le devis › « Préparer le chantier »).'
                  : 'Les chantiers de vos sites apparaîtront ici dès qu’ils seront préparés.'} />
            : (
              <div className="liste">
                {liste.data.map((c) => (
                  <Link key={c.id} to={`/interventions/chantiers/${c.id}`} className="liste-element ch-ligne">
                    <span className="liste-element-corps">
                      <span className="liste-element-titre">{c.reference ? <span className="mono">{c.reference}</span> : null}{c.reference ? ' — ' : ''}{c.title}</span>
                      <span className="petit discret">
                        {c.site_name}{c.devis_number ? <> — devis <span className="mono">{c.devis_number}</span></> : ' — sans devis'}
                        {` — ${pluriel(c.nb_postes, 'poste')}, ${pluriel(c.nb_etapes, 'étape')}`}
                        {c.responsable ? ` — ${c.responsable}` : ''}{c.planned_start ? ` — début prévu le ${date(c.planned_start)}` : ''}
                      </span>
                    </span>
                    <span className="int-ligne-badges">
                      {c.demo && <Badge ton="ambre" icone={<FlaskConical size={14} aria-hidden />}>Démo</Badge>}
                      <BadgeStatutChantier statut={c.status} />
                    </span>
                    <span className="tres-petit discret masque-etroit int-ligne-date">{depuis(c.updated_at)}</span>
                  </Link>
                ))}
              </div>
            )}
        </Panneau>
      )}
      {creation && <DialogueSansDevis fermer={() => setCreation(false)} />}
    </div>
  );
}
